import { Controller } from "@hotwired/stimulus"
import debounce from "lodash/debounce"

export default class extends Controller {
  static targets = [
    "input",
    "hiddenInput",
    "results"
  ]

  static values = {
    searchType: String,
    patientId: String,
    savedUserId: String
  }

  connect() {
    // Solo inicializar la funcionalidad de búsqueda si el input no está deshabilitado
    if (!this.inputTarget.disabled) {
      this.search = debounce(this.search.bind(this), 300)
      this.closeResults()
      // Agregar event listener para clicks fuera del componente
      document.addEventListener('click', this.handleClickOutside.bind(this))

      // Add input event listener to handle manual clearing
      this.inputTarget.addEventListener('input', this.handleInputChange.bind(this))

      // Add blur event listener to assign manual text to hidden input for backend validation
      this.inputTarget.addEventListener('blur', this.handleBlur.bind(this))

      // Check if current user matches saved user on load
      this.checkInitialValidation()
    }
  }

  disconnect() {
    // Solo remover event listener si se inicializó
    if (!this.inputTarget.disabled) {
      document.removeEventListener('click', this.handleClickOutside.bind(this))
      this.inputTarget.removeEventListener('input', this.handleInputChange.bind(this))
      this.inputTarget.removeEventListener('blur', this.handleBlur.bind(this))
    }
  }

  handleClickOutside(event) {
    // Verificar si el click fue fuera del input y de los resultados
    if (!this.element.contains(event.target)) {
      this.closeResults()
    }
  }

  handleInputChange(event) {
    const query = event.target.value.trim()

    // If user field is cleared, reset hidden input and remove validation classes
    if (!query || query.length < 2) {
      this.hiddenInputTarget.value = ''
      this.inputTarget.classList.remove('is-valid', 'is-invalid')
    } else {
      // If there's a query but no user selected, remove validation classes
      if (!this.hiddenInputTarget.value) {
        this.inputTarget.classList.remove('is-valid', 'is-invalid')
      }
    }
  }

  handleBlur(event) {
    const query = event.target.value.trim()

    // Si escribió algo manualmente pero no seleccionó de la lista
    if (query && !this.hiddenInputTarget.value) {
      // Limpiar el campo y el hidden input
      this.inputTarget.value = ''
      this.hiddenInputTarget.value = ''

      // Mostrar mensaje de error temporal
      this.showTemporaryError('Debe seleccionar un paciente válido de la lista')
    }
  }

  showTemporaryError(message) {
    // Remover error anterior si existe
    const existingError = this.element.querySelector('.temporary-error')
    if (existingError) {
      existingError.remove()
    }

    // Crear mensaje de error temporal
    const errorDiv = document.createElement('div')
    errorDiv.className = 'invalid-feedback d-block temporary-error'
    errorDiv.textContent = message

    // Insertar después del input
    this.inputTarget.parentNode.appendChild(errorDiv)

    // Remover el error después de 3 segundos
    setTimeout(() => {
      if (errorDiv.parentNode) {
        errorDiv.remove()
      }
    }, 3000)
  }

  search() {
    if (this.inputTarget.disabled) return
    const query = this.inputTarget.value.trim()

    // Hide results if input is empty or too short
    if (!query || query.length < 2) {
      this.hiddenInputTarget.value = ''
      this.resultsTarget.classList.add('d-none')
      return
    }

    // Get patient ID to exclude from search
    const patientId = this.patientIdValue || ''

    // Fetch search results
    fetch(`/user_searches/search_patients?query=${encodeURIComponent(query)}&patient_id=${encodeURIComponent(patientId)}&search_type=${encodeURIComponent(this.searchTypeValue)}`)
      .then(response => response.text())
      .then(html => {
        this.resultsTarget.innerHTML = html
        this.resultsTarget.classList.remove('d-none')
      })
      .catch(error => {
        console.error('Error searching users:', error)
        this.resultsTarget.innerHTML = '<div class="p-2 text-muted">Error en la búsqueda</div>'
        this.resultsTarget.classList.remove('d-none')
      })
  }

  selectGuardian(event) {
    this.selectUser(event, 'guardian')
  }

  selectBillingResponsible(event) {
    this.selectUser(event, 'billing_responsible')
  }

  selectTemplateContact(event) {
    this.selectUser(event, 'template_contact')

    // Autocompletar campos relacionados al paciente
    const userId = event.currentTarget.dataset.userId
    const userName = event.currentTarget.dataset.userName

    // Buscar y completar campo "nombre_paciente" si existe
    const nombrePacienteField = document.querySelector('input[name="parameters[nombre_paciente]"]')
    if (nombrePacienteField) {
      nombrePacienteField.value = userName
      // Disparar evento para actualizar preview
      nombrePacienteField.dispatchEvent(new Event('input', { bubbles: true }))
    }

    // También buscar campo "nombre" si existe
    const nombreField = document.querySelector('input[name="parameters[nombre]"]')
    if (nombreField) {
      nombreField.value = userName
      nombreField.dispatchEvent(new Event('input', { bubbles: true }))
    }

    // Generar link de onboarding si el template lo requiere
    this.generateOnboardingLink(userId)
  }

  generateOnboardingLink(patientId) {
    // Verificar si existe el campo link_de_onboarding
    // Si no existe, significa que el template no tiene este parámetro
    const onboardingLinkField = document.querySelector('input[name="parameters[link_de_onboarding]"]')
    if (!onboardingLinkField) {
      return // El template no tiene este parámetro, no hacer nada
    }

    // Mostrar indicador de carga
    onboardingLinkField.disabled = true
    onboardingLinkField.value = 'Generando link...'
    onboardingLinkField.classList.add('border-warning')

    // Obtener CSRF token
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    // Llamar al endpoint para generar el link
    // Usar solo el body para POST, no incluir patient_id en URL para evitar logging de datos sensibles
    fetch('/whatsapp_templates/generate_onboarding_link_for_patient', {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json',
        'X-CSRF-Token': csrfToken,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ patient_id: patientId })
    })
    .then(response => response.json())
    .then(data => {
      onboardingLinkField.disabled = false
      if (data.success && data.link) {
        onboardingLinkField.value = data.link
        onboardingLinkField.classList.remove('border-warning')
        onboardingLinkField.classList.add('border-success')
        // Disparar evento para actualizar preview
        onboardingLinkField.dispatchEvent(new Event('input', { bubbles: true }))
      } else {
        onboardingLinkField.value = ''
        onboardingLinkField.classList.remove('border-warning', 'border-success')
        onboardingLinkField.classList.add('border-danger')
        const errorMessage = data.error || 'Error al generar el link de onboarding'
        console.error('Error generating onboarding link:', errorMessage)
        // Mostrar mensaje de error al usuario
        this.showErrorMessage(onboardingLinkField, errorMessage)
      }
    })
    .catch(error => {
      onboardingLinkField.disabled = false
      onboardingLinkField.value = ''
      onboardingLinkField.classList.remove('border-warning', 'border-success')
      onboardingLinkField.classList.add('border-danger')
      console.error('Error generating onboarding link:', error)
      // Mostrar mensaje de error al usuario
      this.showErrorMessage(onboardingLinkField, 'Error de conexión. Por favor, intenta nuevamente.')
    })
  }

  selectPatient(event) {
    this.selectUser(event, 'sales')
  }

  selectUser(event, type) {
    event.preventDefault()
    const userId = event.currentTarget.dataset.userId
    const userName = event.currentTarget.dataset.userName
    const userCuit = event.currentTarget.dataset.userCuit || ''
    const savedUserId = this.savedUserIdValue

    this.inputTarget.value = userName
    this.hiddenInputTarget.value = userId
    this.resultsTarget.classList.add('d-none')

    // Limpiar cualquier error temporal
    const existingError = this.element.querySelector('.temporary-error')
    if (existingError) {
      existingError.remove()
    }

    // Only mark as valid if the selected user matches the saved one
    this.inputTarget.classList.remove('is-invalid', 'is-valid')
    if (savedUserId && userId === savedUserId) {
      this.inputTarget.classList.add('is-valid')
    }

    // Dispatch event so other controllers can react to patient selection
    this.dispatch("selected", { detail: { userId, userName, type, userCuit } })
  }

  focus() {
    if (this.inputTarget.disabled) return

    // Si el dropdown ya está abierto, cerrarlo
    if (!this.resultsTarget.classList.contains("d-none")) {
      this.closeResults()
      return
    }

    // Si el input está vacío o tiene menos de 2 caracteres, mostrar todos los usuarios
    if (this.inputTarget.value.length < 2) {
      // Get patient ID to exclude from search
      const patientId = this.patientIdValue || ''

      fetch(`/user_searches/search_patients?patient_id=${encodeURIComponent(patientId)}&search_type=${encodeURIComponent(this.searchTypeValue)}`)
        .then(response => response.text())
        .then(html => {
          this.resultsTarget.innerHTML = html
          this.resultsTarget.classList.remove("d-none")
        })
        .catch(error => {
          console.error('Error loading users:', error)
          this.resultsTarget.innerHTML = '<div class="p-2 text-muted">Error cargando usuarios</div>'
          this.resultsTarget.classList.remove("d-none")
        })
    } else {
      // Si tiene 2 o más caracteres, hacer la búsqueda
      this.search()
    }
  }

  closeResults() {
    if (this.hasResultsTarget) {
      this.resultsTarget.classList.add("d-none")
    }
  }

  checkInitialValidation() {
    const savedUserId = this.savedUserIdValue
    const currentUserId = this.hiddenInputTarget.value

    // Only mark as valid if there's a saved user and it matches the current selection
    if (savedUserId && currentUserId && savedUserId === currentUserId) {
      this.inputTarget.classList.add('is-valid')
    }
  }

  showErrorMessage(field, message) {
    // Remover mensajes de error anteriores
    const existingError = field.parentElement.querySelector('.error-message')
    if (existingError) {
      existingError.remove()
    }

    // Crear y mostrar mensaje de error
    const errorDiv = document.createElement('div')
    errorDiv.className = 'error-message text-danger small mt-1'
    errorDiv.textContent = message
    field.parentElement.appendChild(errorDiv)

    // Remover el mensaje después de 5 segundos
    setTimeout(() => {
      if (errorDiv.parentElement) {
        errorDiv.remove()
      }
    }, 5000)
  }
}
